import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Alert,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Collapse,
  Breadcrumbs,
  TablePagination,
  Chip,
  Skeleton,
  IconButton,
  Menu,
  useMediaQuery,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import {
  MoreVert as MoreVertIcon,
  Home as HomeIcon,
  NavigateNext as NavigateNextIcon,
} from '@mui/icons-material'
import { ROUTES } from '@/utils/constants'
import { PageHelpPopover } from '@/components/common/PageHelpPopover'
import { FiltersButton } from '@/components/common/FiltersButton'
import { NewsEditorDialog } from '@/components/admin/NewsEditorDialog'
import { NewsReadsDialog } from '@/components/admin/NewsReadsDialog'
import { newsApi, type NewsListItem, type NewsTag } from '@/api/news'
import { getErrorMessage } from '@/utils/error'
import { formatDateDMY } from '@/utils/date'

type SortValue = 'created_desc' | 'created_asc' | 'published_desc' | 'title_asc'

const SORT_MAP: Record<SortValue, { sort: 'created_at' | 'published_at' | 'title'; order: 'asc' | 'desc' }> = {
  created_desc: { sort: 'created_at', order: 'desc' },
  created_asc: { sort: 'created_at', order: 'asc' },
  published_desc: { sort: 'published_at', order: 'desc' },
  title_asc: { sort: 'title', order: 'asc' },
}

export function AdminNewsPage() {
  const { t } = useTranslation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const [items, setItems] = useState<NewsListItem[]>([])
  const [tags, setTags] = useState<NewsTag[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  const [search, setSearch] = useState('')
  const [sort, setSort] = useState<SortValue>('created_desc')
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [publishedFilter, setPublishedFilter] = useState<'all' | 'published' | 'draft'>('all')
  const [tagFilter, setTagFilter] = useState<number | 'all'>('all')

  /**
   * Счётчик для кнопки «Фильтры»: под Collapse спрятаны статус публикации и
   * тег. Поиск и сортировка стоят отдельными полями в тулбаре — считать их
   * нельзя, иначе бейдж горел бы при пустой панели фильтров.
   */
  const appliedFiltersCount = useMemo(() => {
    let count = 0
    if (publishedFilter !== 'all') count += 1
    if (tagFilter !== 'all') count += 1

    return count
  }, [publishedFilter, tagFilter])

  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [meta, setMeta] = useState({ current_page: 1, last_page: 1, per_page: 10, total: 0 })

  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)
  const [menuItem, setMenuItem] = useState<NewsListItem | null>(null)

  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [readsDialogOpen, setReadsDialogOpen] = useState(false)
  const [readsNews, setReadsNews] = useState<NewsListItem | null>(null)

  const loadNews = async () => {
    setLoading(true)
    setError(null)
    try {
      const { sort: sortField, order } = SORT_MAP[sort]
      const result = await newsApi.getList({
        search: search.trim() || undefined,
        is_published: publishedFilter === 'all' ? undefined : publishedFilter === 'published',
        tag_id: tagFilter === 'all' ? undefined : tagFilter,
        sort: sortField,
        order,
        page: page + 1,
        per_page: rowsPerPage,
      })
      setItems(result.data)
      setMeta(result.meta)
    } catch (err) {
      setError(getErrorMessage(err))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadNews()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, search, publishedFilter, tagFilter, sort])

  useEffect(() => {
    newsApi
      .getTags()
      .then(setTags)
      .catch(() => {})
  }, [])

  const openCreateDialog = () => {
    setEditingId(null)
    setDialogOpen(true)
  }

  const openEditDialog = (item: NewsListItem) => {
    setEditingId(item.id)
    setDialogOpen(true)
  }

  const handleSaved = async () => {
    setSuccess(t(editingId ? 'admin.news.updated' : 'admin.news.created') as string)
    await loadNews()
  }

  const handleTogglePublish = async (item: NewsListItem) => {
    setError(null)
    setSuccess(null)
    try {
      if (item.is_published) {
        await newsApi.unpublish(item.id)
        setSuccess(t('admin.news.unpublished') as string)
      } else {
        await newsApi.publish(item.id)
        setSuccess(t('admin.news.published') as string)
      }
      await loadNews()
    } catch (err) {
      setError(getErrorMessage(err))
    }
  }

  const handlePreview = async (item: NewsListItem) => {
    setError(null)
    try {
      const url = await newsApi.getPreviewLink(item.id)
      window.open(url, '_blank', 'noopener')
    } catch (err) {
      setError(getErrorMessage(err))
    }
  }

  const handleDelete = async (item: NewsListItem) => {
    if (!window.confirm(t('admin.news.deleteConfirm', { title: item.title }) as string)) return
    setError(null)
    setSuccess(null)
    try {
      await newsApi.delete(item.id)
      setSuccess(t('admin.news.deleted') as string)
      await loadNews()
    } catch (err) {
      setError(getErrorMessage(err))
    }
  }

  const openMenu = (event: React.MouseEvent<HTMLElement>, item: NewsListItem) => {
    event.stopPropagation()
    setMenuAnchor(event.currentTarget)
    setMenuItem(item)
  }

  // menuItem обнуляется только после закрытия меню (см. TransitionProps.onExited),
  // иначе подпись пункта «Опубликовать / Снять с публикации» моргает при закрытии
  const closeMenu = () => {
    setMenuAnchor(null)
  }

  const statusChip = (item: NewsListItem) => (
    <Chip
      size="small"
      color={item.is_published ? 'success' : 'default'}
      label={item.is_published ? t('admin.news.statusPublished') : t('admin.news.statusDraft')}
    />
  )

  const tagChips = (item: NewsListItem) => (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
      {(item.tags ?? []).map((tag) => (
        <Chip key={tag.id} size="small" label={tag.name} sx={{ bgcolor: tag.color, color: '#fff' }} />
      ))}
    </Box>
  )

  return (
    <DashboardLayout>
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          aria-label="breadcrumb"
          sx={{ mb: 2 }}
        >
          <Link
            to={ROUTES.DASHBOARD}
            style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', color: 'inherit' }}
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            {t('dashboard.home')}
          </Link>
          <Typography color="text.primary">{t('dashboard.administration')}</Typography>
          <Typography color="text.primary">{t('admin.news.title')}</Typography>
        </Breadcrumbs>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 3 }}>
          <Typography variant="h4" component="h1" fontWeight={700} sx={{ flexGrow: 1 }}>
            {t('admin.news.title')}
          </Typography>
          <PageHelpPopover pageKey="admin-news" />
        </Box>

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Toolbar: поиск + сортировка + фильтры + создание */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            size="small"
            label={t('admin.news.search')}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value)
              setPage(0)
            }}
            sx={{ minWidth: { xs: '100%', sm: 240 } }}
          />
          <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 220 } }}>
            <InputLabel>{t('admin.news.sort')}</InputLabel>
            <Select
              label={t('admin.news.sort')}
              value={sort}
              onChange={(e) => setSort(e.target.value as SortValue)}
            >
              <MenuItem value="created_desc">{t('admin.news.sortCreatedDesc')}</MenuItem>
              <MenuItem value="created_asc">{t('admin.news.sortCreatedAsc')}</MenuItem>
              <MenuItem value="published_desc">{t('admin.news.sortPublishedDesc')}</MenuItem>
              <MenuItem value="title_asc">{t('admin.news.sortTitleAsc')}</MenuItem>
            </Select>
          </FormControl>
          <FiltersButton
            open={filtersOpen}
            count={appliedFiltersCount}
            onToggle={() => setFiltersOpen((o) => !o)}
            label={t('admin.news.filtersButton')}
          />
          <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
            <Button variant="contained" size="small" onClick={openCreateDialog} sx={{ height: 40 }}>
              {t('admin.news.create')}
            </Button>
          </Box>
        </Box>

        {/* Фильтры */}
        <Collapse in={filtersOpen}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 2 }}>
            <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 180 } }}>
              <InputLabel>{t('admin.news.filterStatus')}</InputLabel>
              <Select
                label={t('admin.news.filterStatus')}
                value={publishedFilter}
                onChange={(e) => {
                  setPublishedFilter(e.target.value as typeof publishedFilter)
                  setPage(0)
                }}
              >
                <MenuItem value="all">{t('admin.news.all')}</MenuItem>
                <MenuItem value="published">{t('admin.news.statusPublished')}</MenuItem>
                <MenuItem value="draft">{t('admin.news.statusDraft')}</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 180 } }}>
              <InputLabel>{t('admin.news.filterTag')}</InputLabel>
              <Select
                label={t('admin.news.filterTag')}
                value={tagFilter}
                onChange={(e) => {
                  const value = e.target.value
                  setTagFilter(value === 'all' ? 'all' : Number(value))
                  setPage(0)
                }}
              >
                <MenuItem value="all">{t('admin.news.all')}</MenuItem>
                {tags.map((tag) => (
                  <MenuItem key={tag.id} value={tag.id}>
                    {tag.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Collapse>

        {loading ? (
          isMobile ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {[1, 2, 3, 4].map((i) => (
                <Paper key={i} sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Skeleton variant="text" width={40} height={20} />
                    <Skeleton variant="circular" width={32} height={32} />
                  </Box>
                  <Skeleton variant="text" width="70%" height={24} />
                  <Skeleton variant="text" width="50%" height={20} sx={{ mt: 0.5 }} />
                  <Skeleton variant="rectangular" width={90} height={24} sx={{ borderRadius: 1, mt: 1 }} />
                </Paper>
              ))}
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>{t('admin.news.columnTitle')}</TableCell>
                    <TableCell>{t('admin.news.columnStatus')}</TableCell>
                    <TableCell>{t('admin.news.columnTags')}</TableCell>
                    <TableCell>{t('admin.news.columnPublishedAt')}</TableCell>
                    <TableCell align="right">{t('admin.news.columnReads')}</TableCell>
                    <TableCell align="right">{t('admin.users.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[1, 2, 3, 4, 5].map((i) => (
                    <TableRow key={i}>
                      <TableCell><Skeleton variant="text" width={24} /></TableCell>
                      <TableCell>
                        <Skeleton variant="text" width={180} />
                        <Skeleton variant="text" width={120} height={18} />
                      </TableCell>
                      <TableCell><Skeleton variant="rectangular" width={90} height={24} sx={{ borderRadius: 1 }} /></TableCell>
                      <TableCell><Skeleton variant="text" width={80} /></TableCell>
                      <TableCell><Skeleton variant="text" width={80} /></TableCell>
                      <TableCell align="right"><Skeleton variant="text" width={30} sx={{ ml: 'auto' }} /></TableCell>
                      <TableCell align="right"><Skeleton variant="circular" width={32} height={32} sx={{ ml: 'auto' }} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )
        ) : meta.total === 0 ? (
          <Paper sx={{ p: 3 }}>
            <Typography color="text.secondary">{t('admin.news.empty')}</Typography>
          </Paper>
        ) : isMobile ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {items.map((item) => (
              <Paper key={item.id} sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    #{item.id}
                  </Typography>
                  <IconButton size="small" onClick={(e) => openMenu(e, item)}>
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </Box>
                <Typography fontWeight={600}>{item.title}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {item.slug}
                </Typography>
                <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  {statusChip(item)}
                  {item.is_pinned && <Chip size="small" color="info" label={t('admin.news.pinned')} />}
                  {item.published_at && (
                    <Typography variant="caption" color="text.secondary">
                      {formatDateDMY(item.published_at)}
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary">
                    {t('admin.news.columnReads')}: {item.reads_count ?? 0}
                  </Typography>
                </Box>
                {(item.tags?.length ?? 0) > 0 && <Box sx={{ mt: 1 }}>{tagChips(item)}</Box>}
              </Paper>
            ))}
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>{t('admin.news.columnTitle')}</TableCell>
                  <TableCell>{t('admin.news.columnStatus')}</TableCell>
                  <TableCell>{t('admin.news.columnTags')}</TableCell>
                  <TableCell>{t('admin.news.columnPublishedAt')}</TableCell>
                  <TableCell align="right">{t('admin.news.columnReads')}</TableCell>
                  <TableCell align="right">{t('admin.users.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>{item.id}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography fontWeight={600}>{item.title}</Typography>
                        {item.is_pinned && (
                          <Chip size="small" color="info" label={t('admin.news.pinned')} />
                        )}
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {item.slug}
                      </Typography>
                    </TableCell>
                    <TableCell>{statusChip(item)}</TableCell>
                    <TableCell>{tagChips(item)}</TableCell>
                    <TableCell>
                      {item.published_at ? formatDateDMY(item.published_at) : '—'}
                    </TableCell>
                    <TableCell align="right">{item.reads_count ?? 0}</TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={(e) => openMenu(e, item)}>
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={closeMenu}
          TransitionProps={{ onExited: () => setMenuItem(null) }}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItem
            onClick={() => {
              if (menuItem) openEditDialog(menuItem)
              closeMenu()
            }}
          >
            {t('admin.news.edit')}
          </MenuItem>
          <MenuItem
            onClick={() => {
              if (menuItem) handleTogglePublish(menuItem)
              closeMenu()
            }}
          >
            {menuItem?.is_published ? t('admin.news.unpublish') : t('admin.news.publish')}
          </MenuItem>
          <MenuItem
            onClick={() => {
              if (menuItem) handlePreview(menuItem)
              closeMenu()
            }}
          >
            {t('admin.news.preview')}
          </MenuItem>
          <MenuItem
            onClick={() => {
              if (menuItem) {
                setReadsNews(menuItem)
                setReadsDialogOpen(true)
              }
              closeMenu()
            }}
          >
            {t('admin.news.reads')}
          </MenuItem>
          <MenuItem
            onClick={() => {
              if (menuItem) handleDelete(menuItem)
              closeMenu()
            }}
          >
            {t('admin.news.delete')}
          </MenuItem>
        </Menu>

        {!loading && meta.total > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, overflow: 'hidden', minWidth: 0 }}>
            <TablePagination
              component="div"
              count={meta.total}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10))
                setPage(0)
              }}
              rowsPerPageOptions={[10, 15, 25, 50]}
              labelRowsPerPage={isMobile ? '' : t('admin.news.rowsPerPage')}
              labelDisplayedRows={({ from, to, count }) =>
                t('admin.news.displayedRows', { from, to, count })
              }
            />
          </Box>
        )}

        <NewsEditorDialog
          open={dialogOpen}
          newsId={editingId}
          tags={tags}
          onClose={() => setDialogOpen(false)}
          onSaved={handleSaved}
        />

        <NewsReadsDialog
          open={readsDialogOpen}
          news={readsNews}
          onClose={() => setReadsDialogOpen(false)}
        />
      </Box>
    </DashboardLayout>
  )
}
